import Header from "@/components/bionexus/Header";
import Footer from "@/components/Footer";
import BackNav from "@/components/BackNav";
import { Link } from "@/components/LocaleLink";
import { useI18n } from "@/i18n/I18nProvider";
import { applicationTypeLabel, italianName, localizedName, translatedText } from "@/i18n/content";
import SeoAlternates from "@/components/SeoAlternates";
import { itemListJsonLd } from "@/lib/structuredData";
import { useApplications } from "@/hooks/useBioNexusData";
import { Helmet } from "react-helmet-async";
import Reveal, { SplitWords } from "@/components/Reveal";
import ApplicationCard from "@/components/cards/ApplicationCard";
import { SectionHeading } from "@/components/detail/DetailLayout";
import { splitImageNote } from "@/lib/text";

const Applications = () => {
  const { data: applications, isLoading } = useApplications();
  const { m, locale } = useI18n();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20">
          <div className="h-[50vh] bg-muted animate-pulse" />
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="aspect-video bg-muted animate-pulse" />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!applications || applications.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 text-center px-6">
          <h1 className="font-display font-medium text-3xl mb-4">{m.applications.emptyTitle}</h1>
          <p className="text-muted-foreground">{m.applications.emptyText}</p>
        </main>
      </div>
    );
  }

  const featuredApp = applications[0];
  const otherApps = applications.slice(1);
  const featuredTitle = localizedName(locale, featuredApp.title, featuredApp.title_cn, italianName(featuredApp, "title"));
  const featuredDesc = translatedText(featuredApp, "description", featuredApp.description, locale);

  return (
    <div className="min-h-screen bg-background page-enter">
      <Helmet>
        <title>{`${m.meta.applicationsTitle} — BioNexus`}</title>
        <meta name="description" content={m.meta.applicationsDescription} />
        <meta property="og:title" content={`${m.meta.applicationsTitle} — BioNexus`} />
        <meta property="og:description" content={m.meta.applicationsDescription} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(itemListJsonLd(locale, "applications", applications.map((x) => ({ id: x.id, name: x.title }))))}
        </script>
      </Helmet>
      <SeoAlternates path="/applications" />
      <Header />
      
        <main className="pt-20">
        {/* Featured application */}
        <section className="on-dark relative h-[64vh] min-h-[460px] overflow-hidden bg-[hsl(156,35%,5%)]">
          {featuredApp.image_url ? (
            <img
              src={featuredApp.image_url}
              alt={featuredApp.title}
              className="hero-settle absolute inset-0 w-full h-full object-cover opacity-60"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-[hsl(156,30%,8%)]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(156,35%,5%)] via-[hsla(156,35%,5%,0.6)] to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsla(156,35%,5%,0.7)] to-transparent" />
          <div className="absolute inset-x-0 bottom-0 pb-14 md:pb-20">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
              <p className="hero-fade mb-5 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.12em] text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                {m.applications.featured} · {applicationTypeLabel(m, featuredApp.application_type)}
              </p>
              <h1 className="font-display font-medium text-[3rem] md:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-white max-w-4xl text-balance mb-6" lang={featuredTitle.primaryLang}>
                <SplitWords text={featuredTitle.primary} baseDelay={100} />
              </h1>
              <p lang={featuredDesc.lang} className="hero-fade text-white/75 max-w-2xl mb-8 line-clamp-2 leading-relaxed" style={{ animationDelay: "400ms" }}>
                {splitImageNote(featuredDesc.text || "").body}
              </p>
              <Link
                to={`/applications/${featuredApp.id}`}
                className="hero-fade group inline-flex items-center gap-3 rounded-full bg-primary px-6 py-3 font-mono text-xs uppercase tracking-[0.12em] text-primary-foreground transition-transform hover:-translate-y-0.5"
                style={{ animationDelay: "550ms" }}
              >
                {m.applications.readTheCase} <span className="arrow-nudge">→</span>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 px-6 lg:px-12">
          <div className="max-w-[1400px] mx-auto">
            <div className="mb-12">
              <BackNav to="/" label={m.common.backHome} />
            </div>

            <SectionHeading
              title={m.applications.all}
              action={
                <span className="font-mono text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                  {m.applications.count(otherApps.length)}
                </span>
              }
            />

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {otherApps.map((app, idx) => (
                <Reveal key={app.id} delay={(idx % 3) * 80} className="h-full">
                  <ApplicationCard application={app} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Applications;
